import { faker } from "@faker-js/faker";
import { action } from "@storybook/addon-actions";
import { Story as GenericStory, Meta } from "@storybook/react";
import { ArrowLeftRight } from "lucide-react";
import React from "react";
import { MemoryRouter, Route, Switch, useHistory } from "react-router-dom";
import Block from "../Block";
import { Button } from "../Button";
import Facets from "../Facets";
import Flex from "../Flex";
import { QueryPagination } from "../Pagination";
import Toggle from "../Toggle";
import { Typography } from "../Typography";
import Table from "./Table";
import { IItemExample, mockItem } from "./__mocks__";
import { ITableProps, TSortingState } from "./types";

faker.seed(1234);

const commonArgs: ITableProps<IItemExample, keyof IItemExample> = {
    items: Array.from(Array(20).keys()).map(mockItem),
    withoutContainerStyles: false,
    variant: "default",
    columnDefinitions: [
        {
            id: "id",
            header: "ID",
            cell: cell => cell.row.original.id
        },
        {
            id: "skuTitle",
            header: "SKU Title",
            accessorKey: "skuTitle"
        },
        {
            id: "fulfillmentCenter",
            header: "Fulfillment Center",
            accessorFn: item => item.fulfillmentCenter
        },
        {
            id: "fulfillmentStatus",
            header: "Fulfillment Status",
            accessorKey: "fulfillmentStatus"
        },
        {
            id: "createdBy",
            header: "Created By",
            accessorKey: "createdBy"
        },
        {
            id: "completedBy",
            header: "Completed By",
            accessorKey: "completedBy"
        }
    ]
};

export default {
    title: "common/Table",
    component: Table,
    args: commonArgs,
    argTypes: {
        variant: {
            control: {
                type: "select",
                options: ["default", "inset"]
            }
        }
    }
} as Meta<typeof commonArgs>;

type Story = GenericStory<typeof commonArgs>;

const DetailsPage = () => {
    const history = useHistory();

    return (
        <>
            <div>Item details</div>
            <Button trackName="go_back" variant="secondary" title="Go back" onClick={() => history.goBack()} />
        </>
    );
};

const Template: Story = args => {
    return (
        <MemoryRouter initialEntries={["/"]}>
            <Switch>
                <Route path="/" exact>
                    <Table {...args} />
                </Route>
                <Route path="/item/:id">
                    <DetailsPage />
                </Route>
            </Switch>
        </MemoryRouter>
    );
};

export const Basic = Template.bind({});

export const InsetVariant = Template.bind({});
InsetVariant.args = {
    variant: "inset"
};

export const WithFacets = Template.bind({});
WithFacets.args = {
    header: <Facets all query="status" names={["Open", "In progress", "Shipped", "Cancelled"] as const} />
};

export const WithFacetsAndActions = Template.bind({});
WithFacetsAndActions.args = {
    header: (
        <Facets
            all
            query="status"
            names={["Open to be fulfilled", "In progress of fulfillment", "Shipped eventually", "Cancelled"] as const}
        />
    ),
    actions: (
        <Flex spacing={1}>
            <Button trackName="download_csv" variant="secondary" title="Download CSV" />
            <Button trackName="reset_filters" variant="secondary" title="Reset filters" />
        </Flex>
    )
};

export const WithLink = Template.bind({});
WithLink.args = {
    getRowLink: item => `/item/${item.id}`
};

export const EmptyState = Template.bind({});
EmptyState.args = {
    items: []
};

export const CustomStringAsEmptyState = Template.bind({});
CustomStringAsEmptyState.args = {
    items: [],
    emptyState: "Table is empty!"
};

export const WithLoader = Template.bind({});
WithLoader.args = {
    loading: true
};

export const WithPagination = Template.bind({});
WithPagination.args = {
    footer: <QueryPagination totalPages={10} query="page" />
};

export const WithCustomRowHeight = Template.bind({});
WithCustomRowHeight.args = {
    rowHeight: 8
};

export const WithCustomTableHeight = Template.bind({});
WithCustomTableHeight.args = {
    tableHeight: 400
};

export const WithCustomTableMaxHeightOverflow = Template.bind({});
WithCustomTableMaxHeightOverflow.args = {
    tableMaxHeight: 400
};

export const WithCustomTableMaxHeightNoOverflow = Template.bind({});
WithCustomTableMaxHeightNoOverflow.args = {
    items: commonArgs.items.slice(0, 9),
    tableMaxHeight: 400
};

export const WithHeader = Template.bind({});
WithHeader.args = {
    header: "My awesome table"
};

export const InsideBlock: Story = args => (
    <Block>
        <Flex column spacing={2}>
            <div>Some block content</div>
            <Table collapsible defaultOpen header="Products purchased" {...args} />
            <div>Some block content</div>
        </Flex>
    </Block>
);
InsideBlock.args = {};

const renderExtraContent = () => (
    <Flex spacing={3} py={32}>
        <Typography variant="label-small-regular" color="text-primary-700">
            Issue&nbsp;ID:&nbsp;
            <Typography as="span" variant="label-small-medium" color="text-primary-1200">
                129750
            </Typography>
        </Typography>
        <Typography variant="label-small-regular" color="text-primary-700">
            Notes: QA request - merchant wants us to check sample of 50 units to see if packaging is sealed. asana card
            here: http://asana.hive/123
        </Typography>
        <Button trackName="resolve" title="Resolve" />
    </Flex>
);

export const Collapsible = Template.bind({});
Collapsible.args = {
    renderExtraContent
};

export const WithoutTableHead = Template.bind({});
WithoutTableHead.args = {
    tableHead: false
};

export const WithColumnDefinitions = Template.bind({});
WithColumnDefinitions.args = {
    disableColumnAutoSizing: true,
    renderExtraContent,
    visibleColumns: {
        createdBy: false,
        completedBy: false
    },
    columnOrder: ["expand", "id", "skuTitle", "fulfillmentCenter", "fulfillmentStatus", "createdBy", "completedBy"]
};

const ToggleAction = () => {
    const [active, setActive] = React.useState(false);

    return <Toggle checked={active} onChange={() => setActive(!active)} trackName="toggle" />;
};

export const WithActions = Template.bind({});
WithActions.args = {
    columnDefinitions: [
        {
            id: "id",
            header: () => "ID",
            cell: cell => cell.row.original.id
        },
        {
            id: "skuTitle",
            header: () => "SKU Title",
            cell: cell => cell.row.original.skuTitle,
            size: 300
        },
        {
            id: "fulfillmentCenter",
            header: () => "Fulfillment Center",
            cell: cell => cell.row.original.fulfillmentCenter
        },
        {
            id: "fulfillmentStatus",
            header: () => "Fulfillment Status",
            cell: () => <ToggleAction />,
            meta: { actions: true }
        },
        {
            id: "createdBy",
            header: () => "Created By",
            cell: cell => cell.row.original.createdBy
        },
        {
            id: "completedBy",
            header: () => "Completed By",
            cell: cell => cell.row.original.completedBy
        }
    ],
    disableColumnAutoSizing: true,
    getRowLink: item => `/item/${item.id}`,
    renderExtraContent: undefined,
    visibleColumns: {
        createdBy: false,
        completedBy: false
    },
    columnOrder: ["expand", "id", "skuTitle", "fulfillmentCenter", "fulfillmentStatus", "createdBy", "completedBy"]
};

export const WithSelect: Story = args => {
    const [selected, setSelected] = React.useState<IItemExample[]>([]);

    return (
        <Flex column spacing={2}>
            {selected.length > 0 && (
                <div>
                    <Typography variant="label-small-regular" color="text-primary-700">
                        Selected items:&nbsp;
                        <Typography as="span" variant="label-small-medium" color="text-primary-1200">
                            {selected.length}
                        </Typography>
                    </Typography>
                </div>
            )}
            <Table {...args} selectedItems={selected} onRowSelectionChange={setSelected} />
        </Flex>
    );
};

WithSelect.args = {
    disableColumnAutoSizing: true,
    renderExtraContent,
    visibleColumns: {
        createdBy: false,
        completedBy: false
    },
    enableRowSelection: row => row.original.fulfillmentStatus !== "Cancelled",
    columnOrder: [
        "select",
        "expand",
        "id",
        "skuTitle",
        "fulfillmentCenter",
        "fulfillmentStatus",
        "createdBy",
        "completedBy"
    ]
};

export const WithSorting: Story = ({ items, ...args }) => {
    const [sorting, setSorting] = React.useState<TSortingState<keyof IItemExample>>({});

    const sortedItems = React.useMemo(() => {
        const sorted = [...items];
        const sortingColumn = Object.keys(sorting)[0] as keyof IItemExample;
        const sortingDirection = sorting[sortingColumn];

        if (sortingColumn && sortingDirection) {
            sorted.sort((a, b) => {
                const aValue = a[sortingColumn];
                const bValue = b[sortingColumn];

                if (aValue === bValue) {
                    return 0;
                }

                if (sortingDirection === "asc") {
                    return aValue < bValue ? -1 : 1;
                }

                return aValue < bValue ? 1 : -1;
            });
        }

        return sorted;
    }, [items, sorting]);

    return (
        <Flex column spacing={2}>
            <Table items={sortedItems} {...args} sorting={sorting} onSortingChange={setSorting} />
        </Flex>
    );
};

WithSorting.args = {
    disableColumnAutoSizing: true,
    columnDefinitions: [
        {
            id: "id",
            header: "ID",
            accessorKey: "id",
            enableSorting: true
        },
        {
            id: "skuTitle",
            header: "SKU Title",
            accessorKey: "skuTitle",
            enableSorting: true
        },
        {
            id: "fulfillmentCenter",
            header: "Fulfillment Center",
            accessorKey: "fulfillmentCenter"
        },
        {
            id: "fulfillmentStatus",
            header: "Fulfillment Status",
            accessorKey: "fulfillmentStatus",
            enableSorting: true
        },
        {
            id: "createdBy",
            header: "Created By",
            accessorKey: "createdBy"
        },
        {
            id: "completedBy",
            header: "Completed By",
            accessorKey: "completedBy"
        }
    ]
};

export const WithBulkActions: Story = args => (
    <>
        <Table {...args} />
        <Flex style={{ position: "fixed", left: "50%", bottom: "20px", transform: "translateX(-50%)" }}>
            <Table.BulkActions.Portal />
        </Flex>
    </>
);

WithBulkActions.args = {
    disableColumnAutoSizing: true,
    renderExtraContent,
    visibleColumns: {
        createdBy: false,
        completedBy: false
    },
    enableRowSelection: row => row.original.fulfillmentStatus !== "Cancelled",
    renderBulkActions: selected => (
        <Table.BulkActions>
            <Table.BulkActions.Item>Selected items: {selected.length}</Table.BulkActions.Item>

            <Table.BulkActions.Item
                icon={<ArrowLeftRight />}
                onClick={() => {
                    action("Bulk action");
                }}
            >
                Bulk action
            </Table.BulkActions.Item>
        </Table.BulkActions>
    ),
    columnOrder: [
        "select",
        "expand",
        "id",
        "skuTitle",
        "fulfillmentCenter",
        "fulfillmentStatus",
        "createdBy",
        "completedBy"
    ]
};
